// New consents (intake) view — Blueprint §2 screen 2 (F-INT-*, F-ASGN promote).
// Ports the SEMANTICS of legacy loadIntake/renderIntake/openIntakeModal/
// execIntakeUpdate/execIntakePromoteWithFile (v20 lines ~1808-1867, 2920-2939,
// 5427-5525) onto the 2.0 data layer (data/intake.js, data/assign.js,
// data/users.js) + the store + the delegated data-action router.
//
// On screen:
//   header + counts → filter tabs (New / Overdue SLA / Duplicates / No PDF /
//   Processed — exact legacy buckets) → rows (avatar, programme dot, name,
//   submitted-ago, duplicate badge, No-PDF badge, SLA pill) → Review primary
//   action opening the review modal.
//
// THE REVIEW MODAL + PROMOTE HANDSHAKE (R-009 + Q1 owner ruling):
//   - consent details, decision-notes field, a REQUIRED "Assign to" owner picker
//     (#intake-owner) from assignableUsers(): nurse/coordinator pre-selected to
//     self, ADMIN no default + helper — Accept BLOCKED until an owner is picked.
//   - Accept → promote_consent_to_case (UNTOUCHED RPC) → parse the honest
//     {case_id, created, matched_existing, already_promoted} result → claim-safe
//     assignCase(at_promote) → honest per-outcome toast. matched_existing must
//     NOT steal an owner (TAKEN → INFO toast). An assign network-fail leaves a
//     sticky "created but not assigned" toast + the case in the legacy pool.
//   - Promote-over-declined, no-PDF, and failed-upload confirms use openModal
//     (uiConfirm) — NEVER native confirm().
//   - EVIDENCE (R-025): staff-entered (bulk/manual) rows require a verifier name
//     + scanned-consent evidence before Accept.
//
// No inline handlers anywhere (strict CSP): data-action only.

import { store, SLICES } from '../state/store.js';
import { esc } from '../ui/esc.js';
import { toast } from '../ui/toast.js';
import { register } from '../ui/actions.js';
import { openModal, closeModal, setBtnBusy } from '../ui/modal.js';
import { netErr, newRef } from '../errors.js';
import { RPCS, TABLES, BUCKETS } from '../db/contract.js';
import { getClient } from '../db/client.js';
import { registerView, navigate, countText } from './registry.js';
import { initials } from './shell.js';
import { progDotHTML } from '../programmes.js';
import { openWorkspace } from './workspace.js';
import { assignCase } from '../data/assign.js';
import { loadUsers, assignableUsers, usersMap, userName } from '../data/users.js';
import {
  loadIntake,
  fetchSubmission,
  uploadConsentFile,
  INTAKE_TABS,
  rowsForIntakeTab,
  requiresEvidence,
  canOwnPatients,
  ownerPickerModel,
  parsePromoteResult,
  promoteToast,
  isAlreadyPromoted,
  isDeclined,
  staleIntake,
  INTAKE_STALE_DAYS,
} from '../data/intake.js';

// ─── SVG icons (no functional emoji per §3; inline, CSP-safe) ───────────────

/** @param {string} paths @param {number} [size] @returns {string} */
function ic(paths, size = 16) {
  return (
    `<svg class="ic" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" ` +
    'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    paths +
    '</svg>'
  );
}
const IC_WARN = '<path d="M12 4L2.5 20h19z"/><path d="M12 10v4m0 3v.5"/>';
const IC_INFO = '<circle cx="12" cy="12" r="9"/><path d="M12 8v5m0 3v.5"/>';
const IC_INBOX = '<path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>';

/** Deterministic avatar colour from a name (self-contained port of legacy ac()). */
function avatarColor(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return `hsl(${h} 42% 42%)`;
}

/** Human "submitted N ago" (compact; mirrors today.js dueLabel direction). */
function agoLabel(d, now) {
  if (!d) return '—';
  const base = now ? now.getTime() : Date.now();
  const diff = (base - new Date(d).getTime()) / 1000;
  if (isNaN(diff)) return '—';
  if (diff < 60) return 'just now';
  if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  if (diff < 86400 * 7) return Math.floor(diff / 86400) + 'd ago';
  return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * What the list may show for an INTAKE slice. PURE. An absent or unresolved
 * slice is 'loading' — never "Queue clear" (review R-04: the empty state was
 * painted from `s.rows || []` before loadIntake() had answered, hiding every
 * pending consent and the stale-intake banner behind a green all-clear).
 * @param {any} slice SLICES.INTAKE value (may be undefined)
 * @returns {'loading'|'error'|'ready'}
 */
export function intakeListState(slice) {
  const s = slice || {};
  if (s.loadError) return 'error';
  if (!s.loaded) return 'loading';
  return 'ready';
}

// ─── Module state ──────────────────────────────────────────────────────────

let _activeTab = 'new';
let _wired = false;
let _promoteBusy = false; // block double-taps → no duplicate promotes (legacy latch)
let _ownerLoadRef = /** @type {string|null} */ (null);

/** Test-only: reset module state. @returns {void} */
export function _resetConsents() {
  _activeTab = 'new';
  _wired = false;
  _promoteBusy = false;
  _ownerLoadRef = null;
}

function doc() {
  return /** @type {any} */ (globalThis).document;
}
function consentsMounted() {
  const d = doc();
  const ui = store.get(SLICES.UI) || {};
  return !!d && ui.view === 'consents' && !!d.getElementById('consents-view');
}
function session() {
  return store.get(SLICES.SESSION) || {};
}
function selfId() {
  const s = session();
  return s.user && s.user.id;
}
function role() {
  return session().role;
}

/** Resolve a submission's programme_id → catalogue row (for the dot). */
function programmeById(id) {
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const list = prog.list || [];
  return list.find((p) => p.programme_id === id) || null;
}

// ─── Renderers (store → DOM) ───────────────────────────────────────────────

function renderTabs() {
  const d = doc();
  const host = d && d.getElementById('consents-tabs');
  if (!host) return;
  const s = store.get(SLICES.INTAKE) || {};
  const counts = s.counts || {};
  // Dash on anything that is not 'ready' — a hard 0 over a failed load is the
  // same false all-clear as a hard 0 over an in-flight one.
  const state = intakeListState(s);
  host.innerHTML = INTAKE_TABS.map((t) => {
    const active = _activeTab === t.id;
    const n = countText(state, counts[t.countKey] || 0);
    return (
      `<button type="button" class="today-tab${active ? ' active' : ''}" data-action="consents-tab" ` +
      `data-tab="${esc(t.id)}" aria-pressed="${active}">${esc(t.label)}` +
      `<span class="tb-n">${esc(String(n))}</span></button>`
    );
  }).join('');
}

function renderList() {
  const d = doc();
  const host = d && d.getElementById('consents-list');
  if (!host) return;
  const s = store.get(SLICES.INTAKE) || {};
  const state = intakeListState(s);

  if (state === 'loading') {
    host.innerHTML =
      '<div aria-busy="true" aria-label="Loading new consents">' +
      '<div class="skel"></div><div class="skel"></div><div class="skel"></div></div>';
    return;
  }

  if (state === 'error') {
    host.innerHTML =
      '<button type="button" class="today-error" data-action="consents-retry">' +
      ic(IC_WARN, 22) +
      '<div><h2>Couldn’t load new consents — tap to retry</h2>' +
      '<p>The query failed or was denied. Nothing is hidden — tap to try again.</p></div></button>';
    return;
  }

  const now = new Date();
  const all = s.rows || [];
  const rows = rowsForIntakeTab(all, _activeTab);

  // Stale-intake banner — shown on EVERY tab, because the rows it warns about
  // are not always on the tab being viewed: an on-hold consent lives under
  // Processed, which is precisely how one waited from January to August. The
  // per-row chips age quietly; this line is the loud summary that must be
  // consciously ignored rather than simply not seen.
  const stale = staleIntake(all, now);
  const staleBanner = stale.n
    ? '<div class="consents-stale" role="status">' +
      ic(IC_WARN, 18) +
      `<div><b>${esc(String(stale.n))} consent${stale.n === 1 ? ' has' : 's have'} waited more than ${INTAKE_STALE_DAYS} days without a decision</b>` +
      ` — oldest ${esc(String(stale.oldestDays))} days${stale.oldestName ? ` (${esc(stale.oldestName)})` : ''}.` +
      ' Each is a person who signed a form and heard nothing. Action or close every one — on-hold counts too.</div></div>'
    : '';

  // The Duplicates tab explains what accepting a duplicate does (legacy dupNote).
  const dupNote =
    _activeTab === 'duplicates'
      ? '<div class="consents-note">Same phone or ID as an existing record — accepting will open the existing patient instead of creating a new one.</div>'
      : '';

  if (!rows.length) {
    host.innerHTML = staleBanner + dupNote + emptyHTML(all.length);
    return;
  }

  host.innerHTML =
    staleBanner +
    dupNote +
    '<div class="consents-rhdr"><span>Submission</span><span>County</span><span>Status</span><span></span></div>' +
    rows.map((sub) => rowHTML(sub, now)).join('');

  // CSP-safe avatar colours (style-src 'self' blocks inline style attrs).
  host.querySelectorAll('.tr-av[data-avatar]').forEach((el) => {
    /** @type {any} */ (el).style.background = el.getAttribute('data-avatar');
  });
}

/** One submission row. @param {any} sub enriched submission @param {Date} now @returns {string} */
function rowHTML(sub, now) {
  const id = sub.submission_id || sub.id;
  const name = sub.patient_name || '—';
  const prog = programmeById(sub.programme_id);
  const dot = prog ? progDotHTML(prog.display_name || prog.code, prog.brand_accent_hex, 8) : '';

  let badges = '';
  if (sub.overdue) badges += '<span class="today-tag danger">Overdue SLA</span> ';
  else if (sub.pending) badges += '<span class="today-tag accent">Within SLA</span> ';
  if (sub.dupInList) badges += '<span class="today-tag warn">Duplicate</span> ';
  if (sub.crossDup)
    badges += `<span class="today-tag danger" title="${esc('Matches ' + (sub.crossDup.match.patient_name || 'an existing patient'))}">${esc(sub.crossDup.reason)}</span> `;
  if (sub.noPdf) badges += '<span class="today-tag warn" title="No signed consent PDF attached — attach the document before promoting">No signed PDF</span> ';
  if (!sub.pending) badges += `<span class="today-tag">${esc(sub.statusLabel)}</span>`;

  return (
    `<div class="consents-row" role="button" tabindex="0" data-kbd-click data-action="consents-review" ` +
    `data-id="${esc(id)}" aria-label="${esc('Review consent — ' + name)}">` +
    '<div class="tr-pc">' +
    `<div class="tr-av" aria-hidden="true" data-avatar="${esc(avatarColor(name))}">${esc(initials(name))}</div>` +
    '<div class="tr-pi">' +
    `<div class="tr-name">${dot}<span>${esc(name)}</span></div>` +
    `<div class="tr-sub"><span>${esc(agoLabel(sub.created_at, now))} · ${esc(sub.patient_phone || 'No phone')}</span>` +
    `<span class="m-only"> ${badges}</span></div>` +
    '</div></div>' +
    `<div class="consents-county">${esc(sub.county || '—')}</div>` +
    `<div class="consents-badges">${badges}</div>` +
    '<div class="tr-cta">' +
    `<button type="button" class="btn sm pri" data-action="consents-review" data-id="${esc(id)}">Review</button>` +
    '</div>' +
    '</div>'
  );
}

/** Distinct empty states: genuinely empty queue vs an empty filter. */
function emptyHTML(totalRows) {
  if (totalRows > 0) {
    const label = (INTAKE_TABS.find((t) => t.id === _activeTab) || { label: 'this filter' }).label;
    return (
      '<div class="today-empty">' +
      ic(IC_INFO, 22) +
      `<h2>No ${esc(label.toLowerCase())} consents</h2>` +
      '<p>Nothing in this filter. Other tabs may still have consents to review.</p>' +
      '</div>'
    );
  }
  return (
    '<div class="today-empty">' +
    ic(IC_INBOX, 22) +
    '<h2>Queue clear</h2>' +
    '<p>No consent submissions are waiting. New submissions from the public form appear here.</p>' +
    '</div>'
  );
}

function renderBody() {
  if (!consentsMounted()) return;
  renderTabs();
  renderList();
}

// ─── The review modal (openIntakeModal port) ───────────────────────────────

/** Build the owner-picker inner HTML from the current staff directory. */
function ownerSelectHTML() {
  const model = ownerPickerModel(assignableUsers(usersMap()), selfId(), canOwnPatients(role()));
  const opts = model.options
    .map((o) => `<option value="${esc(o.uid)}"${o.selected ? ' selected' : ''}>${esc(o.label)}</option>`)
    .join('');
  const helper = model.canOwn
    ? ''
    : '<div class="consents-owner-help">Admins can’t own patients — choose the nurse who will.</div>';
  return `<select id="intake-owner"><option value="">Choose the owning nurse…</option>${opts}</select>${helper}`;
}

/** Owner-list load-failure block (staff directory never loaded) + Retry. */
function ownerLoadFailHTML() {
  if (!_ownerLoadRef) _ownerLoadRef = newRef();
  return (
    '<div class="consents-owner-fail">' +
    `<span>${ic(IC_WARN, 15)} Staff list didn’t load.</span>` +
    '<button type="button" class="btn sm" data-action="consents-owner-retry">Retry</button>' +
    `<span class="consents-owner-ref">ref ${esc(_ownerLoadRef)}</span>` +
    '</div>'
  );
}

/**
 * Open the consent review modal for a submission id (exported so the Today
 * queue's consent-review rows can open it directly).
 * @param {string} id submission_id
 * @returns {Promise<void>}
 */
export async function openReviewModal(id) {
  if (!id || id === 'undefined') return;
  const sub = await fetchSubmission(id);
  if (!sub) {
    toast('Record not found', 'err');
    return;
  }
  // Ensure the staff directory is present so the owner picker is not a dead-end.
  if (!assignableUsers(usersMap()).length) await loadUsers();

  const prog = programmeById(sub.programme_id);
  const progLine = prog ? esc(prog.display_name || prog.code) : 'Not specified';

  const pdfLink = sub.signed_pdf_url
    ? `<button type="button" class="linklike" data-action="consents-view-pdf" data-path="${esc(sub.signed_pdf_url)}">View attached consent ↗</button>`
    : '';
  const noPdfNotice = sub.signed_pdf_url
    ? ''
    : '<div class="consents-warn">No signed consent PDF is on file. Attach the signed document before promoting.</div>';

  const crossDup = sub.crossDup
    ? `<div class="consents-warn">${ic(IC_WARN, 15)} Possible duplicate — ${esc(sub.crossDup.reason)} as ${esc(sub.crossDup.match.patient_name || 'an existing patient')}. Accepting may open the existing record.</div>`
    : '';

  // R-025: staff-entered (bulk/manual) rows require a verifier name.
  const needsEvidence = requiresEvidence(sub);
  const verifierField = needsEvidence
    ? '<div class="field full"><label>Verifier name <span class="req">*</span></label>' +
      '<input id="intake-verifier" placeholder="Who confirmed the scanned consent?"/>' +
      '<div class="hint">This is a staff-entered / bulk consent — a named verifier and the scanned consent are required before promoting (R-025).</div></div>'
    : '';

  const ownerHasList = assignableUsers(usersMap()).length;
  const ownerField =
    '<div class="field full"><label>Assign to <span class="req">*</span></label>' +
    `<div id="intake-owner-wrap">${ownerHasList ? ownerSelectHTML() : ownerLoadFailHTML()}</div></div>`;

  const attachField =
    '<div class="field full"><label>Attach or replace consent document</label>' +
    `<input type="file" id="intake-consent-file" accept=".pdf,.jpg,.jpeg,.png,.heic"/> ${pdfLink}${noPdfNotice}</div>`;

  const header =
    '<div class="consents-modal-head">' +
    `<div class="tr-av lg" aria-hidden="true" id="intake-av">${esc(initials(sub.patient_name))}</div>` +
    '<div class="consents-modal-id">' +
    `<div class="consents-modal-name">${esc(sub.patient_name || '—')}</div>` +
    `<div class="tr-sub"><span>${esc(sub.patient_phone || 'No phone')} · ${esc(sub.county || 'No county')} · ${esc(agoLabel(sub.created_at, new Date()))}</span></div>` +
    '</div></div>';

  const facts =
    `<div class="consents-fact"><b>Programme:</b> ${progLine}</div>` +
    `<div class="consents-fact"><b>Facility:</b> ${esc(sub.treating_facility || 'Not specified')}</div>` +
    (sub.patient_alt_phone ? `<div class="consents-fact"><b>Alt phone:</b> ${esc(sub.patient_alt_phone)}</div>` : '') +
    (sub.preferred_language ? `<div class="consents-fact"><b>Language:</b> ${esc(sub.preferred_language)}</div>` : '');

  const body =
    header +
    crossDup +
    `<div class="consents-facts">${facts}</div>` +
    attachField +
    verifierField +
    '<div class="field full"><label>Decision notes</label>' +
    '<textarea id="intake-reason" placeholder="Reasoning, hold conditions, or any notes…"></textarea></div>' +
    ownerField;

  openModal('Review consent', sub.patient_name || '', body, [
    { l: 'Decline', v: 'danger', k: 'decline', fn: () => void execUpdate(id, 'declined') },
    { l: 'Hold', k: 'hold', fn: () => void execUpdate(id, 'on_hold') },
    { l: 'Accept & create patient record', v: 'pri', k: 'accept', fn: () => void execPromote(id) },
  ]);

  // Colour the header avatar via CSSOM (CSP blocks inline style attrs).
  const d = doc();
  const av = d && d.getElementById('intake-av');
  if (av) /** @type {any} */ (av).style.background = avatarColor(sub.patient_name);
}

/** Retry loading the staff directory into the owner picker (legacy retryOwnerList). */
async function onOwnerRetry(el) {
  if (el) {
    el.setAttribute('disabled', 'true');
    el.textContent = 'Loading…';
  }
  await loadUsers();
  const wrap = doc() && doc().getElementById('intake-owner-wrap');
  if (!assignableUsers(usersMap()).length) {
    if (el) {
      el.removeAttribute('disabled');
      el.textContent = 'Retry';
    }
    toast('Staff list still didn’t load — check your connection', 'err');
    return;
  }
  if (wrap) wrap.innerHTML = ownerSelectHTML();
}

// ─── Decline / Hold (update_consent_status) ─────────────────────────────────

/**
 * Mark a consent Declined or On-Hold (legacy execIntakeUpdate). A decline needs
 * a reason; a failed save keeps the modal open (setBtnBusy pattern).
 * @param {string} id submission_id
 * @param {'declined'|'on_hold'} statusEnum
 */
async function execUpdate(id, statusEnum) {
  const d = doc();
  const reason = (d && d.getElementById('intake-reason') && d.getElementById('intake-reason').value.trim()) || '';
  if (!reason && statusEnum === 'declined') {
    toast('Reason required to decline', 'err');
    return;
  }
  const label = statusEnum === 'declined' ? 'Declined' : 'On hold';
  const matchKey = statusEnum === 'declined' ? 'decline' : 'hold';
  setBtnBusy(true, 'Updating…', matchKey);
  const { error } = await getClient().rpc(RPCS.UPDATE_CONSENT_STATUS, {
    p_consent_id: id,
    p_status: statusEnum,
    p_reason: reason,
  });
  if (error) {
    setBtnBusy(false);
    toast(netErr(error, 'Could not update consent'), 'err');
    return;
  }
  closeModal();
  toast('Marked as ' + label, 'ok');
  await refresh();
}

// ─── uiConfirm (openModal-based; native confirm() is BANNED, R-015) ─────────

/**
 * A focus-trapped yes/no confirm over the shared modal. Resolves true on
 * confirm, false on cancel. NOTE: this REPLACES the review modal content (one
 * modal host); the promote flow captures the owner pick + notes + file BEFORE
 * calling this, so nothing typed is lost on a "yes".
 * @param {{title: string, message: string, confirmLabel: string, danger?: boolean}} o
 * @returns {Promise<boolean>}
 */
function uiConfirm(o) {
  return new Promise((resolve) => {
    openModal(o.title, '', `<p class="consents-confirm">${esc(o.message)}</p>`, [
      { l: 'Cancel', k: 'cancel', fn: () => { closeModal(); resolve(false); } },
      { l: o.confirmLabel, v: o.danger ? 'danger' : 'pri', k: 'confirm', fn: () => { closeModal(); resolve(true); } },
    ]);
  });
}

// ─── Accept → promote handshake (R-009 + Q1 owner ruling) ───────────────────

/**
 * Accept a consent: validate the owner pick, run the evidence + confirm gates,
 * call the UNTOUCHED promote_consent_to_case RPC, then fill the owner claim-safe
 * and voice the honest outcome. A busy latch blocks double-taps.
 * @param {string} id submission_id
 */
async function execPromote(id) {
  if (_promoteBusy) return;
  _promoteBusy = true;
  try {
    const d = doc();

    // Q1: read + validate the owner pick FIRST — the modal is still open here, so
    // a blocked Accept keeps every typed input intact (no busy, no close).
    const ownerSel = d && d.getElementById('intake-owner');
    const pickedUid = ownerSel ? ownerSel.value : '';
    // HARD gate — a missing select or a placeholder-only list means the staff
    // list never loaded: BLOCK as a load failure, not user error.
    if (!ownerSel || (!pickedUid && ownerSel.options.length <= 1)) {
      toast('Staff list didn’t load — check your connection and reopen this consent', 'err');
      return;
    }
    if (!pickedUid) {
      toast('Choose the owning nurse', 'err');
      return;
    }

    // Capture everything from the (still-open) review modal before any confirm
    // can replace it.
    const fileEl = d && d.getElementById('intake-consent-file');
    const file = fileEl && fileEl.files && fileEl.files[0];
    const verifierEl = d && d.getElementById('intake-verifier');
    const verifier = (verifierEl && verifierEl.value.trim()) || '';

    // Resolve the row (cache → view) for the status + no-PDF gates. A compliance
    // gate must FAIL CLOSED: if the row can't be re-loaded (cache miss + a DB read
    // that threw / returned nothing) every downstream `if (sub && …)` gate would be
    // silently skipped — the evidence (R-025), already-promoted, declined-confirm
    // and no-PDF checks all bypassed. Block the Accept instead.
    const sub = await fetchSubmission(id);
    if (!sub) {
      toast('Could not re-load this consent — reopen and try again', 'err');
      return;
    }

    // R-025 evidence gate (staff-entered / bulk rows): a verifier name AND
    // scanned-consent evidence (existing PDF or a file being attached now).
    if (requiresEvidence(sub)) {
      if (!verifier) {
        toast('Enter the verifier name before promoting (R-025)', 'err');
        return;
      }
      if (!sub.signed_pdf_url && !file) {
        toast('Attach the scanned consent before promoting (R-025)', 'err');
        return;
      }
    }

    // Double-promote guard: only PROMOTED is already-processed (On Hold / Declined
    // stay acceptable; Declined gets a conscious confirm below).
    if (isAlreadyPromoted(sub.status)) {
      closeModal();
      toast('This consent was already accepted — refreshing the queue', 'err');
      await refresh();
      return;
    }
    if (isDeclined(sub.status)) {
      const go = await uiConfirm({
        title: 'Promote a declined consent?',
        message: 'This consent was DECLINED. Promoting it will create a patient record anyway. This cannot be undone from here.',
        confirmLabel: 'Promote anyway',
        danger: true,
      });
      if (!go) return;
    }

    // Upload an attached file, then guard the no-PDF case.
    if (file) {
      toast('Uploading consent…');
      const up = await uploadConsentFile(file, id, { name: sub.patient_name });
      if (!up.ok) {
        const go = await uiConfirm({
          title: 'Consent upload failed',
          message: 'The signed PDF failed to upload. Promote WITHOUT it? Cancel to leave the consent pending and retry.',
          confirmLabel: 'Promote without it',
          danger: true,
        });
        if (!go) return;
      } else if (up.data && up.data.pointerFailed) {
        // The scan is in the bucket but the consent row's pointer update failed:
        // the promote proceeds, but the nurse must know the link didn't land so
        // they can re-attach it (legacy v20 surfaced exactly this).
        toast('File stored, but linking to the consent failed — re-attach if it does not appear', 'err');
      }
    } else if (!sub.signed_pdf_url) {
      const go = await uiConfirm({
        title: 'No signed PDF on file',
        message: 'This consent has no signed PDF attached. Promote anyway? The scanned consent should be attached from the patient record afterwards.',
        confirmLabel: 'Promote anyway',
        danger: true,
      });
      if (!go) return;
    }

    // All gates passed — close the review modal (idempotent if a confirm already
    // closed it) and promote.
    closeModal();
    toast('Promoting…');
    const { data, error } = await getClient().rpc(RPCS.PROMOTE_CONSENT_TO_CASE, { p_consent_id: id });
    if (error) {
      toast(netErr(error, 'Could not promote consent'), 'err');
      return;
    }

    const { caseId, created, matched, already } = parsePromoteResult(data);

    // Q1 post-promote hook: fill the owner claim-safe. matched_existing can be a
    // case that ALREADY has an owner — onlyIfUnassigned means we never steal it
    // (TAKEN is INFO, not an error). A network fail here leaves a real patient
    // unowned: sticky toast + the Legacy pool is the catch-net.
    let assign = /** @type {'ok'|'taken'|'fail'|'none'} */ ('none');
    let takenName = /** @type {string|null} */ (null);
    if (caseId && !already && pickedUid) {
      // No note at promote: this is the FIRST owner assignment, and assignCase
      // hard-labels any note '--- REASSIGN ---' — which would mislabel the intake
      // decision-notes on a brand-new patient's timeline. v_20's promote handshake
      // passed no note here, so omitting it also keeps the write byte-compatible.
      const res = await assignCase(caseId, pickedUid, { onlyIfUnassigned: true, at_promote: true, kind: 'promote' });
      if (res.ok) assign = 'ok';
      else if (res.code === 'TAKEN') {
        assign = 'taken';
        try {
          const { data: oc } = await getClient().from(TABLES.CASES).select('assigned_to').eq('case_id', caseId).maybeSingle();
          if (oc && oc.assigned_to) takenName = userName(oc.assigned_to);
        } catch {
          /* best-effort owner name */
        }
      } else assign = 'fail';
    }

    const toSelf = !!pickedUid && pickedUid === selfId();
    const plan = promoteToast({
      created,
      matched,
      already,
      caseId,
      assign,
      toSelf,
      ownerName: userName(pickedUid),
      takenName,
    });

    if (plan.sticky) {
      // toast() is single-slot; the reloads below can stomp a sticky message.
      // Re-issue it after they settle so the nurse actually learns the patient
      // is unowned (legacy 5515-5519).
      Promise.allSettled([refresh()]).then(() => toast(plan.msg, 'err', 15000));
    } else {
      toast(plan.msg, plan.kind, plan.kind === '' ? 6000 : undefined);
      await refresh();
    }

    if (caseId) void openWorkspace(caseId);
    else navigate('board');
  } finally {
    _promoteBusy = false;
  }
}

// ─── View PDF (signed URL) ──────────────────────────────────────────────────

async function onViewPdf(el) {
  const path = el.getAttribute('data-path');
  if (!path) return;
  try {
    const { data, error } = await getClient().storage.from(BUCKETS.CONSENT_FORMS).createSignedUrl(path, 60);
    if (error || !data || !data.signedUrl) {
      toast(netErr(error, 'Could not open the document'), 'err');
      return;
    }
    const w = /** @type {any} */ (globalThis).open;
    if (w) w(data.signedUrl, '_blank', 'noopener');
  } catch (e) {
    toast(netErr(e, 'Could not open the document'), 'err');
  }
}

// ─── Actions + wiring ───────────────────────────────────────────────────────

async function refresh() {
  const res = await loadIntake(); // returned so Sync can see a failed refresh
  renderBody();
  return res;
}

function onTab(el) {
  const tab = el.getAttribute('data-tab');
  if (!tab || !INTAKE_TABS.some((t) => t.id === tab)) return;
  _activeTab = tab;
  renderBody();
}

function onReview(el) {
  const id = el.getAttribute('data-id');
  if (id) void openReviewModal(id);
}

function wireOnce() {
  if (_wired) return;
  _wired = true;
  register('consents-tab', onTab);
  register('consents-retry', () => void refresh());
  register('consents-review', onReview);
  register('consents-owner-retry', (el) => void onOwnerRetry(el));
  register('consents-view-pdf', (el) => void onViewPdf(el));
  store.subscribe(SLICES.INTAKE, () => renderBody());
  // Realtime: reload when the consents feed changes (legacy 'intake' key).
  store.subscribe(SLICES.RT_CHANGES, (v) => {
    if (consentsMounted() && v && Array.isArray(v.changed) && v.changed.includes('intake')) void refresh();
  });
}

function skeleton() {
  return (
    '<div id="consents-view" class="consents">' +
    '<header class="view-head"><h1>New consents</h1>' +
    '<p class="consents-sub">New submissions awaiting review. Accept a consent to create a patient record and assign its owning nurse.</p></header>' +
    '<div class="today-card">' +
    '<div id="consents-tabs" class="today-tabs" role="group" aria-label="Filter consents"></div>' +
    '<div id="consents-list"></div>' +
    '</div></div>'
  );
}

/**
 * Register the New consents view, replacing the Phase-1 placeholder. Call once
 * at boot.
 * @returns {void}
 */
export function registerConsents() {
  registerView('consents', {
    render(host) {
      host.innerHTML = skeleton();
      wireOnce();
      renderBody();
    },
    load() {
      // Staff directory first (owner picker), then the queue. loadUsers never
      // throws; a miss just degrades the picker to its retry state. Returned
      // (not `void`) so Sync can await both — see F-NAV-03.
      return Promise.all([
        loadUsers().then(() => {
          if (consentsMounted()) renderBody();
        }),
        refresh(),
      ]);
    },
  });
}
